import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.refs.zoomDialogComponent) {
      this.addEventListener('touchstart', this.#handlePinchStart, { passive: false, signal });
      this.addEventListener('touchmove', this.#handlePinchMove, { passive: false, signal });
      // Safari's own pinch gesture, which would zoom the whole page.
      this.addEventListener('gesturestart', this.#handlePinchMove, { passive: false, signal });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Same photos in the same order (a size change): keep this gallery, so the shopper stays on the photo they chose.
    // A colour change puts its own photo first, so the order differs and the gallery is swapped.
    if (MediaGallery.#photoKey(newMediaGallery) === MediaGallery.#photoKey(this)) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * The gallery's slide photos in order, as one string.
   * @param {Element} gallery
   * @returns {string}
   */
  static #photoKey(gallery) {
    return [...gallery.querySelectorAll('slideshow-slides slideshow-slide')]
      .map((slide) => slide.querySelector('img')?.getAttribute('src')?.split('?')[0] ?? slide.getAttribute('slide-id') ?? '')
      .join('|');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * A two-finger pinch on a photo opens the zoom view, as the magnifier does, instead of zooming the whole page.
   * One-finger swipes and scrolling are left to the browser.
   * @param {TouchEvent} event
   */
  #handlePinchStart = (event) => {
    if (event.touches.length !== 2 || !(event.target instanceof Element)) return;
    if (event.target.closest('dialog')) return;

    const index = this.#zoomableIndex(event.target);
    if (index === null) return;

    event.preventDefault();
    this.refs.zoomDialogComponent?.open(index, event);
  };

  /** @param {Event} event */
  #handlePinchMove = (event) => {
    if (event instanceof TouchEvent && event.touches.length < 2) return;
    if (!(event.target instanceof Element) || event.target.closest('dialog')) return;
    if (this.#zoomableIndex(event.target) === null) return;
    if (event.cancelable) event.preventDefault();
  };

  /**
   * Index of the zoomable photo an element sits in, or null.
   * @param {Element} element
   * @returns {number | null}
   */
  #zoomableIndex(element) {
    const slide = element.closest('[data-zoom-index]');
    if (slide) return Number(slide.getAttribute('data-zoom-index'));
    const opener = element.closest('[ref="media[]"]')?.querySelector('[on\\:click^="/zoom/"]');
    const index = Number(opener?.getAttribute('on:click')?.split('/').pop());
    return opener && Number.isInteger(index) ? index : null;
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
